import React, { useEffect, useState, useRef, useCallback } from "react";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimeField } from "@mui/x-date-pickers/DateTimeField";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Badge,
  Divider,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TablePagination,
  Paper,
  Checkbox,
  FormControlLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  EventSeat as SeatIcon,
  WorkspacePremium as PromotedIcon,
  InfoOutlined as InfoIcon,
  ArrowBackIosNew as BackIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import MultipleSelectChip from "../components/ui/MultipleSelectChip.js";
import axios from "../utils/axios";
import { toast } from "react-toastify";
import LocationPicker from "components/LocationPicker.js";

const getMediaType = (url) => {
  if (!url) return 'image';
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase();
  const videoExts = ['mp4', 'mov', 'webm', 'mpeg', 'avi', 'mkv'];
  return videoExts.includes(ext) ? 'video' : 'image';
};

const OpportunityManagement = () => {
  // No dual mode for offers
  const [activeTab, setActiveTab] = useState("management"); // 'management' | 'settings'
  const [opportunities, setOpportunities] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState("LBK Team");
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState(null);
  const [dialogType, setDialogType] = useState("add");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isEditingView, setIsEditingView] = useState(false);
  const [viewFormData, setViewFormData] = useState({});
  const [savingEvent, setSavingEvent] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationSearchLoading, setLocationSearchLoading] = useState(false);
  const locationSearchTimer = useRef(null);

  const themeColor = "#6D53F4";
  const themeColorHover = "#5A3FE0";

  // ===== EVENT OPTIONS (dynamic) =====
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [newCategoryValue, setNewCategoryValue] = useState("");
  const [newEventTypeValue, setNewEventTypeValue] = useState("");
  const [newCurrencyValue, setNewCurrencyValue] = useState("");

  // Debounced Nominatim search for location autocomplete
  const handleLocationSearch = useCallback((inputValue) => {
    if (locationSearchTimer.current) {
      clearTimeout(locationSearchTimer.current);
    }

    if (!inputValue || inputValue.length < 3) {
      setLocationSuggestions([]);
      return;
    }

    setLocationSearchLoading(true);
    locationSearchTimer.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          format: "jsonv2",
          addressdetails: "1",
          q: inputValue,
          limit: "5",
        });
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?${params.toString()}`,
          {
            method: "GET",
            headers: { Accept: "application/json" },
          }
        );
        if (!response.ok) throw new Error("Search failed");
        const results = await response.json();
        setLocationSuggestions(
          results.map((r) => ({
            label: r.display_name,
            lat: parseFloat(r.lat),
            lng: parseFloat(r.lon),
            address: r,
          }))
        );
      } catch (err) {
        console.error("Location autocomplete error:", err);
        setLocationSuggestions([]);
      } finally {
        setLocationSearchLoading(false);
      }
    }, 300);
  }, []);

  // Fetch event options from API
  const fetchEventOptions = useCallback(async () => {
    try {
      setOptionsLoading(true);
      const [catRes, typeRes, curRes] = await Promise.all([
        axios.get('/opportunity-options?group=category'),
        axios.get('/opportunity-options?group=eventType'),
        axios.get('/opportunity-options?group=currency'),
      ]);
      setCategoryOptions(catRes.data.data || []);
      setEventTypeOptions(typeRes.data.data || []);
      setCurrencyOptions(curRes.data.data || []);
    } catch (err) {
      console.error('Failed to fetch event options', err);
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEventOptions();
  }, [fetchEventOptions]);

  // Derived: only active options for the Create/Edit forms
  const availableCategories = categoryOptions.filter((o) => o.isActive).map((o) => o.value);
  const activeCurrencies = currencyOptions.filter((o) => o.isActive);
  const activeEventTypes = eventTypeOptions.filter((o) => o.isActive);

  // ===== Opportunity Settings CRUD helpers =====
  const handleAddOption = async (group, value, clearFn) => {
    if (!value.trim()) return;
    try {
      await axios.post('/opportunity-options', {
        group,
        label: value.trim(),
        value: value.trim(),
      });
      toast.success('Option added');
      clearFn("");
      fetchEventOptions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add option');
    }
  };

  const handleToggleActive = async (option) => {
    try {
      await axios.put(`/opportunity-options/${option._id}`, { isActive: !option.isActive });
      fetchEventOptions();
    } catch (err) {
      toast.error('Failed to toggle status');
    }
  };

  const handleDeleteOption = async (option) => {
    try {
      await axios.delete(`/opportunity-options/${option._id}`);
      toast.success('Option deleted');
      fetchEventOptions();
    } catch (err) {
      toast.error('Failed to delete option');
    }
  };

  // for multiple categories selecion
  const handleCategoryChange = (event) => {
    const {
      target: { value },
    } = event;

    setFormData((prev) => ({
      ...prev,
      categories: typeof value === "string" ? value.split(",") : value,
    }));
  };

  // Pagination states
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(0); // MUI uses 0-based index
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    location: {
      city: "",
      state: "",
      country: "",
      fullAddress: "",
      lat: null,
      lng: null,
    },
    
    categories: [],
    priceRangeMin: "",
    priceCurrencyMin: "Currency",
    priceRangeMax: "",
    priceCurrencyMax: "Currency",
    discountRange: "",
    finalPrice: "",
    bookingLink: "",
    manageEventType: "",
    occupancy: "",
    mapLocationSearch: "",
    mapSearchTrigger: "",
    mapUrl: "",
    eventType: "REGULAR",
    status: "",
    images: [],
  });

  const calculateFinalPriceString = (min, max, discount, currencyMin, currencyMax) => {
    const minVal = parseFloat(min) || 0;
    const maxVal = parseFloat(max) || 0;
    const discVal = parseFloat(discount) || 0;

    if (!min && !max) return "";

    const curr = (currencyMin && currencyMin !== "Currency") ? currencyMin : (currencyMax && currencyMax !== "Currency" ? currencyMax : "INR");
    
    let parts = [];
    if (min) {
      const discountedMin = minVal - (minVal * discVal) / 100;
      parts.push(`Min Price : ${curr} ${discountedMin.toFixed(2)}`);
    }
    if (max) {
      const discountedMax = maxVal - (maxVal * discVal) / 100;
      parts.push(`Max Price : ${curr} ${discountedMax.toFixed(2)}`);
    }

    return parts.join(" - ");
  };

  useEffect(() => {
    const newFinalPrice = calculateFinalPriceString(
      formData.priceRangeMin,
      formData.priceRangeMax,
      formData.discountRange,
      formData.priceCurrencyMin,
      formData.priceCurrencyMax
    );
    if (newFinalPrice !== formData.finalPrice) {
      setFormData((prev) => ({ ...prev, finalPrice: newFinalPrice }));
    }
  }, [formData.priceRangeMin, formData.priceRangeMax, formData.discountRange, formData.priceCurrencyMin, formData.priceCurrencyMax, formData.finalPrice]);

  useEffect(() => {
    if (!isEditingView) return;
    const newFinalPrice = calculateFinalPriceString(
      viewFormData.priceRangeMin,
      viewFormData.priceRangeMax,
      viewFormData.discountRange,
      viewFormData.priceCurrencyMin,
      viewFormData.priceCurrencyMax
    );
    if (newFinalPrice !== viewFormData.finalPrice) {
      setViewFormData((prev) => ({ ...prev, finalPrice: newFinalPrice }));
    }
  }, [viewFormData.priceRangeMin, viewFormData.priceRangeMax, viewFormData.discountRange, viewFormData.priceCurrencyMin, viewFormData.priceCurrencyMax, isEditingView, viewFormData.finalPrice]);

  /* ================= fetch opportunities ================= */
  useEffect(() => {
    fetchOpportunities();
  }, [page, rowsPerPage]);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `/opportunities?page=${page + 1}&limit=${rowsPerPage}`,
      );
      setOpportunities(res.data.opportunities || res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Failed to fetch opportunities");
      setOpportunities([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  //Handling rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  /* ================= DIALOG HANDLING ================= */
  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      startDate: "",
      endDate: "",
      location: {
        city: "",
        state: "",
        country: "",
        fullAddress: "",
        lat: null,
        lng: null,
      },
      
      categories: [],
      priceRangeMin: "",
      priceCurrencyMin: "Currency",
      priceRangeMax: "",
      priceCurrencyMax: "Currency",
      discountRange: "",
      finalPrice: "",
      bookingLink: "",
      manageEventType: "",
      occupancy: "",
      mapLocationSearch: "",
      mapSearchTrigger: "",
      mapUrl: "",
      eventType: "REGULAR",
      status: "",
      images: [],
    });
  };

  const handleOpenDialog = (type, event = null) => {
    setDialogType(type);
    setSelectedEvent(event);

    if (type === "clone" && event) {
      const parsedStartDate = dayjs(event.startDate);
      const parsedEndDate = dayjs(event.endDate);

      setFormData({
        title: event.title || "",
        description: event.description || "",
        startDate: parsedStartDate.isValid() ? parsedStartDate.toISOString() : "",
        endDate: parsedEndDate.isValid() ? parsedEndDate.toISOString() : "",
        location: {
          city: event.location?.city,
          state: event.location?.state,
          country: event.location?.country,
          fullAddress: event.location?.fullAddress || event.location?.city,
          lat: event.location?.geo?.coordinates?.[1],
          lng: event.location?.geo?.coordinates?.[0],
        },
        
        categories: event.categories || [],
        priceRangeMin: event.priceRangeMin || "",
        priceCurrencyMin: event.priceCurrencyMin || "Currency",
        priceRangeMax: event.priceRangeMax || "",
        priceCurrencyMax: event.priceCurrencyMax || "Currency",
        discountRange: event.discountRange || "",
        finalPrice: event.finalPrice || event.price || "",
        bookingLink: event.bookingLink || "",
        manageEventType: event.manageEventType || "",
        occupancy: event.occupancy || "",
        mapLocationSearch: event.mapLocationSearch || "",
        mapUrl: event.mapUrl || "",
        eventType: event.eventType || "REGULAR",
        status: event.status,
        images: (event.images || []).map((img, index) => ({
          ...img,
          filename: img.filename || img.url?.split('/').pop() || `image_${index}`,
          type: img.type || getMediaType(img.url),
        })),
      });
    } else {
      resetForm();
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEvent(null);
    resetForm();
  };

  const buildViewFormData = (event) => ({
    title: event.title || "",
    description: event.description || "",
    categories: event.categories || [],
    price: event.price || "",
    occupancy: event.occupancy || "",
    startDate: event.startDate || "",
    endDate: event.endDate || "",
    bookingLink: event.bookingLink || "",
    mapUrl: event.mapUrl || "",
    discountRange: event.discountRange || "",
    finalPrice: event.finalPrice || event.price || "",
    priceRangeMin: event.priceRangeMin || "",
    priceCurrencyMin: event.priceCurrencyMin || "Currency",
    priceRangeMax: event.priceRangeMax || "",
    priceCurrencyMax: event.priceCurrencyMax || "Currency",
    manageEventType: event.manageEventType || event.eventType || "",
    
    status: event.status || "",
    eventType: event.eventType || "REGULAR",
    location: {
      city: event.location?.city || "",
      state: event.location?.state || "",
      country: event.location?.country || "",
      fullAddress: event.location?.fullAddress || "",
      lat: event.location?.geo?.coordinates?.[1] || null,
      lng: event.location?.geo?.coordinates?.[0] || null,
    },
    images: event.images || [],
    mapLocationSearch: event.mapLocationSearch || event.location?.fullAddress || "",
    mapSearchTrigger: "",
  });

  const handleOpenViewDialog = (event) => {
    setSelectedEvent(event);
    setIsEditingView(false);
    setViewFormData(buildViewFormData(event));
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedEvent(null);
    setIsEditingView(false);
    setViewFormData({});
  };

  const handleViewInputChange = (e) => {
    const { name, value } = e.target;
    setViewFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleViewCategoryChange = (event) => {
    const { target: { value } } = event;
    setViewFormData((prev) => ({
      ...prev,
      categories: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleSaveEditedEvent = async () => {
    // Validation: Max price should not be less than Min price
    const minVal = parseFloat(viewFormData.priceRangeMin) || 0;
    const maxVal = parseFloat(viewFormData.priceRangeMax) || 0;
    if (viewFormData.priceRangeMin && viewFormData.priceRangeMax && maxVal < minVal) {
      toast.error("Max price cannot be less than Min price");
      return;
    }

    try {
      setSavingEvent(true);
      const payload = {
        ...viewFormData,
        occupancy: Number(viewFormData.occupancy),
        price: (() => {
          const min = parseFloat(viewFormData.priceRangeMin) || 0;
          const discount = parseFloat(viewFormData.discountRange) || 0;
          return min - (min * discount) / 100;
        })(),
        finalPrice: (() => {
          const min = parseFloat(viewFormData.priceRangeMin) || 0;
          const discount = parseFloat(viewFormData.discountRange) || 0;
          return min - (min * discount) / 100;
        })(),
        location: {
          ...viewFormData.location,
          state: viewFormData.location.state || viewFormData.location.city,
          geo: {
            type: "Point",
            coordinates: [
              viewFormData.location.lng || 0,
              viewFormData.location.lat || 0,
            ],
          },
        },
      };
      await axios.put(`/opportunities/${selectedEvent._id}`, payload);
      toast.success("opportunity updated successfully");
      setIsEditingView(false);
      fetchOpportunities();
      handleCloseViewDialog();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update opportunity");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleOpenDeleteDialog = (event) => {
    setOpportunityToDelete(event);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setOpportunityToDelete(null);
  };

  const handleDeleteOpportunity = async () => {
    try {
      await axios.delete(`/opportunities/${opportunityToDelete._id}`);
      toast.success("opportunity deleted successfully");
      handleCloseDeleteDialog();
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete opportunity");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ================= IMAGE UPLOAD ================= */
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploadingImage(true);

    try {
      const uploadData = new FormData();
      files.forEach((file) => uploadData.append("images", file));

      const res = await axios.post("/upload/multiple", uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!res.data?.success || !Array.isArray(res.data.data)) {
        throw new Error("Invalid upload response");
      }

      setFormData((prev) => {
        const newImages = res.data.data.map((img, index) => ({
          url: img.url,
          filename: img.filename || img.url?.split('/').pop() || `upload_${Date.now()}_${index}`,
          type: img.type || 'image',
          isPrimary: prev.images.length === 0 && index === 0,
        }));

        return {
          ...prev,
          images: [...prev.images, ...newImages],
        };
      });

      toast.success("Images uploaded");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => {
      const images = [...prev.images];
      const wasPrimary = images[index]?.isPrimary;
      images.splice(index, 1);

      if (wasPrimary && images.length) {
        images[0].isPrimary = true;
      }

      return { ...prev, images };
    });
  };

  const handleSetPrimaryImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.map((img, i) => ({
        ...img,
        isPrimary: i === index,
      })),
    }));
  };

  /* ================= Save Opportunity ================= */
  const handleSubmit = async () => {
    // Validation: Max price should not be less than Min price
    const minVal = parseFloat(formData.priceRangeMin) || 0;
    const maxVal = parseFloat(formData.priceRangeMax) || 0;
    if (formData.priceRangeMin && formData.priceRangeMax && maxVal < minVal) {
      toast.error("Max price cannot be less than Min price");
      return;
    }

    try {
      const payload = {
        ...formData,
        occupancy: Number(formData.occupancy),
        price: (() => {
          const min = parseFloat(formData.priceRangeMin) || 0;
          const discount = parseFloat(formData.discountRange) || 0;
          return min - (min * discount) / 100;
        })(),
        finalPrice: (() => {
          const min = parseFloat(formData.priceRangeMin) || 0;
          const discount = parseFloat(formData.discountRange) || 0;
          return min - (min * discount) / 100;
        })(),
        location: {
          ...formData.location,
          state: formData.location.state || formData.location.city, // fallback
          geo: {
            type: "Point",
            coordinates: [formData.location.lng, formData.location.lat],
          },
        },
      };

      // Both "add" and "clone" create a new event via POST
      await axios.post("/opportunities", payload);
      toast.success(dialogType === "clone" ? "opportunity cloned successfully" : "opportunity created");

      handleCloseDialog();
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to Save Opportunity");
    }
  };

  /* ================= HELPERS ================= */
  const getOpportunityImage = (event) => {
    if (event.images?.length) {
      const primary = event.images.find((i) => i.isPrimary);
      return primary?.url || event.images[0].url;
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "UPCOMING":
        return { bg: "#E3F2FD", text: "#1976D2" };
      case "ONGOING":
        return { bg: "#E8F5E9", text: "#388E3C" };
      case "COMPLETED":
        return { bg: "#F3E5F5", text: "#7B1FA2" };
      case "CANCELLED":
        return { bg: "#FFEBEE", text: "#C62828" };
      default:
        return { bg: "#E0E0E0", text: "#616161" };
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  /* ================= UI ================= */
  return (
    <Box sx={{ p: 3 }}>
      {/* ================= TOP TAB BAR ================= */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" gap={2}>
          <Typography variant="h5" fontWeight={700} color="#111827">
            Opportunity Management
          </Typography>
        </Box>
        {activeTab === "management" && (
          <Button
            variant="contained"
            endIcon={<AddIcon />}
            onClick={() => handleOpenDialog("add")}
            sx={{
              borderRadius: "24px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              fontSize: "0.95rem",
              background: "linear-gradient(135deg, #8B5CF6 0%, #E879A0 50%, #F0A060 100%)",
              color: "#fff",
              boxShadow: "0 4px 14px rgba(139, 92, 246, 0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #7C3AED 0%, #D16690 50%, #E09050 100%)",
                boxShadow: "0 6px 20px rgba(139, 92, 246, 0.45)",
              },
            }}
          >
            Create Opportunity
          </Button>
        )}
      </Box>

      {/* ================= EVENT MANAGEMENT / EVENT SETTINGS BUTTONS ================= */}
      <Box display="flex" gap={3} mb={3}>
        <Button
          variant="text"
          startIcon={<TuneIcon />}
          onClick={() => setActiveTab("management")}
          sx={{
            textTransform: "none",
            fontWeight: activeTab === "management" ? 700 : 500,
            px: 1,
            py: 0.5,
            fontSize: "0.95rem",
            borderRadius: 0,
            color: activeTab === "management" ? themeColor : "#9CA3AF",
            borderBottom: activeTab === "management" ? `2.5px solid ${themeColor}` : "2.5px solid transparent",
            "&:hover": {
              bgcolor: "transparent",
              color: themeColor,
            },
          }}
        >
          Opportunity Management
        </Button>
        <Button
          variant="text"
          startIcon={<SettingsIcon />}
          onClick={() => setActiveTab("settings")}
          sx={{
            textTransform: "none",
            fontWeight: activeTab === "settings" ? 700 : 500,
            px: 1,
            py: 0.5,
            fontSize: "0.95rem",
            borderRadius: 0,
            color: activeTab === "settings" ? themeColor : "#9CA3AF",
            borderBottom: activeTab === "settings" ? `2.5px solid ${themeColor}` : "2.5px solid transparent",
            "&:hover": {
              bgcolor: "transparent",
              color: themeColor,
            },
          }}
        >
          Opportunity Settings
        </Button>
      </Box>

      {/* ================= EVENT SETTINGS SCREEN ================= */}
      {activeTab === "settings" && (
        <Box>
          {optionsLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : (
            <Grid container spacing={3}>
              {/* ---- CATEGORIES SECTION ---- */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ borderRadius: "16px", p: 2.5, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", height: "100%" }}>
                  <Typography variant="subtitle1" fontWeight={700} color="#111827" mb={2}>
                    Opportunity Categories
                  </Typography>

                  {/* Add new */}
                  <Box display="flex" gap={1} mb={2}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Add new category"
                      value={newCategoryValue}
                      onChange={(e) => setNewCategoryValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleAddOption("category", newCategoryValue, setNewCategoryValue); }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#F9FAFB" } }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleAddOption("category", newCategoryValue, setNewCategoryValue)}
                      disabled={!newCategoryValue.trim()}
                      sx={{ bgcolor: themeColor, color: "#fff", borderRadius: "10px", width: 38, height: 38, "&:hover": { bgcolor: themeColorHover }, "&.Mui-disabled": { bgcolor: "#E5E7EB", color: "#9CA3AF" } }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* List */}
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    {categoryOptions.map((opt) => (
                      <Box
                        key={opt._id}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ py: 0.8, px: 1.5, borderRadius: "10px", "&:hover": { bgcolor: "#F3F4F6" }, transition: "background 0.15s" }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Checkbox
                            checked={opt.isActive}
                            onChange={() => handleToggleActive(opt)}
                            size="small"
                            sx={{ p: 0, color: "#D1D5DB", "&.Mui-checked": { color: themeColor } }}
                          />
                          <Typography variant="body2" sx={{ color: opt.isActive ? "#111827" : "#9CA3AF", textDecoration: opt.isActive ? "none" : "line-through", fontWeight: 500 }}>
                            {opt.value}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => handleDeleteOption(opt)} sx={{ color: "#D1D5DB", p: 0.3, "&:hover": { color: "#EF4444" } }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ))}
                    {categoryOptions.length === 0 && (
                      <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>No categories yet</Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* ---- EVENT TYPES SECTION ---- */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ borderRadius: "16px", p: 2.5, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", height: "100%" }}>
                  <Typography variant="subtitle1" fontWeight={700} color="#111827" mb={2}>
                    Opportunity Types
                  </Typography>

                  {/* Add new */}
                  <Box display="flex" gap={1} mb={2}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Add new opportunity type"
                      value={newEventTypeValue}
                      onChange={(e) => setNewEventTypeValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleAddOption("eventType", newEventTypeValue, setNewEventTypeValue); }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#F9FAFB" } }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleAddOption("eventType", newEventTypeValue, setNewEventTypeValue)}
                      disabled={!newEventTypeValue.trim()}
                      sx={{ bgcolor: themeColor, color: "#fff", borderRadius: "10px", width: 38, height: 38, "&:hover": { bgcolor: themeColorHover }, "&.Mui-disabled": { bgcolor: "#E5E7EB", color: "#9CA3AF" } }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* List */}
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    {eventTypeOptions.map((opt) => (
                      <Box
                        key={opt._id}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ py: 0.8, px: 1.5, borderRadius: "10px", "&:hover": { bgcolor: "#F3F4F6" }, transition: "background 0.15s" }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Checkbox
                            checked={opt.isActive}
                            onChange={() => handleToggleActive(opt)}
                            size="small"
                            sx={{ p: 0, color: "#D1D5DB", "&.Mui-checked": { color: themeColor } }}
                          />
                          <Typography variant="body2" sx={{ color: opt.isActive ? "#111827" : "#9CA3AF", textDecoration: opt.isActive ? "none" : "line-through", fontWeight: 500 }}>
                            {opt.value}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => handleDeleteOption(opt)} sx={{ color: "#D1D5DB", p: 0.3, "&:hover": { color: "#EF4444" } }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ))}
                    {eventTypeOptions.length === 0 && (
                      <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>No Opportunity Types yet</Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* ---- CURRENCIES SECTION ---- */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ borderRadius: "16px", p: 2.5, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", height: "100%" }}>
                  <Typography variant="subtitle1" fontWeight={700} color="#111827" mb={2}>
                    Currencies
                  </Typography>

                  {/* Add new */}
                  <Box display="flex" gap={1} mb={2}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Add new currency (e.g. GBP)"
                      value={newCurrencyValue}
                      onChange={(e) => setNewCurrencyValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleAddOption("currency", newCurrencyValue, setNewCurrencyValue); }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#F9FAFB" } }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleAddOption("currency", newCurrencyValue, setNewCurrencyValue)}
                      disabled={!newCurrencyValue.trim()}
                      sx={{ bgcolor: themeColor, color: "#fff", borderRadius: "10px", width: 38, height: 38, "&:hover": { bgcolor: themeColorHover }, "&.Mui-disabled": { bgcolor: "#E5E7EB", color: "#9CA3AF" } }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* List */}
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    {currencyOptions.map((opt) => (
                      <Box
                        key={opt._id}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ py: 0.8, px: 1.5, borderRadius: "10px", "&:hover": { bgcolor: "#F3F4F6" }, transition: "background 0.15s" }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Checkbox
                            checked={opt.isActive}
                            onChange={() => handleToggleActive(opt)}
                            size="small"
                            sx={{ p: 0, color: "#D1D5DB", "&.Mui-checked": { color: themeColor } }}
                          />
                          <Typography variant="body2" sx={{ color: opt.isActive ? "#111827" : "#9CA3AF", textDecoration: opt.isActive ? "none" : "line-through", fontWeight: 500 }}>
                            {opt.value}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => handleDeleteOption(opt)} sx={{ color: "#D1D5DB", p: 0.3, "&:hover": { color: "#EF4444" } }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ))}
                    {currencyOptions.length === 0 && (
                      <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>No currencies yet</Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* ================= EVENT TABLE (Management view) ================= */}
      {activeTab === "management" && (
        <Box>
          <Box display="flex" justifyContent="flex-end" gap={2} mb={3}>
            <Button
              variant={activeSubTab === "LBK Team" ? "contained" : "outlined"}
              onClick={() => setActiveSubTab("LBK Team")}
              sx={{
                borderRadius: "24px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 0.8,
                boxShadow: "none",
                ...(activeSubTab === "LBK Team"
                  ? {
                    bgcolor: themeColor,
                    color: "#fff",
                    "&:hover": { bgcolor: themeColorHover, boxShadow: "none" },
                  }
                  : {
                    bgcolor: "transparent",
                    color: "#374151",
                    border: "1px solid #D1D5DB",
                    "&:hover": {
                      bgcolor: "#F9FAFB",
                    },
                  }),
              }}
            >
              LBK Team
            </Button>
            <Button
              variant={activeSubTab === "External" ? "contained" : "outlined"}
              onClick={() => setActiveSubTab("External")}
              sx={{
                borderRadius: "24px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 0.8,
                boxShadow: "none",
                ...(activeSubTab === "External"
                  ? {
                    bgcolor: themeColor,
                    color: "#fff",
                    "&:hover": { bgcolor: themeColorHover, boxShadow: "none" },
                  }
                  : {
                    bgcolor: "transparent",
                    color: "#374151",
                    border: "1px solid #D1D5DB",
                    "&:hover": {
                      bgcolor: "#F9FAFB",
                    },
                  }),
              }}
            >
              External
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ px: 1, width: 80 }}>Opportunity Poster</TableCell>
              <TableCell sx={{ px: 1, width: 100 }}>Title</TableCell>
              <TableCell sx={{ px: 1, width: 130 }}>Description</TableCell>
              <TableCell sx={{ px: 1, width: 140 }}>Posted Date & Time</TableCell>
              <TableCell sx={{ px: 1, width: 100 }}>Location</TableCell>
              <TableCell sx={{ px: 1, width: 70 }}>Occupancy</TableCell>
              <TableCell sx={{ px: 1, width: 140 }}>Opportunity Date & time</TableCell>
              <TableCell sx={{ px: 1, width: 70 }}>Opportunity Fair</TableCell>
              <TableCell sx={{ px: 1, width: 80 }}>Status</TableCell>
              <TableCell sx={{ px: 1, width: 110 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {opportunities.map((event) => (
              <TableRow key={event._id}>
                {/* Opportunity Poster */}
                <TableCell sx={{ px: 1 }}>
                  <Box
                    sx={{
                      height: 45,
                      width: 70,
                      border: "1px solid #E0E0E0",
                      borderRadius: 1,
                      backgroundImage: getOpportunityImage(event)
                        ? `url(${getOpportunityImage(event)})`
                        : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      position: "relative",
                    }}
                  >
                    {event.isPromoted && (
                      <Tooltip title="Promoted Opportunity" arrow>
                        <PromotedIcon
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            color: "#FFD700",
                            cursor: "pointer",
                            fontSize: 14,
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                {/* Title */}
                <TableCell sx={{ px: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {event.title.charAt(0).toUpperCase() + event.title.slice(1)}
                  </Typography>
                </TableCell>
                {/* Description */}
                <TableCell sx={{ px: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {event.description}
                  </Typography>
                </TableCell>
                {/* Posted Date & Time (createdAt) */}
                <TableCell sx={{ px: 1 }}>
                  <Typography variant="body2" noWrap>
                    {dayjs(event.createdAt).isValid()
                      ? dayjs(event.createdAt).format("DD/MM/YYYY hh:mm A")
                      : "-"}
                  </Typography>
                </TableCell>
                {/* Location */}
                <TableCell sx={{ px: 1 }}>
                  <Box display="flex" gap={0.5} alignItems="center">
                    <LocationIcon sx={{ color: "#757575", fontSize: 16 }} />
                    <Typography variant="body2" noWrap>
                      {event.location?.city || event.location?.country || "-"}
                    </Typography>
                  </Box>
                </TableCell>
                {/* Occupancy */}
                <TableCell sx={{ px: 1 }}>
                  <Typography variant="body2">
                    {event.occupancy || "-"}
                  </Typography>
                </TableCell>
                {/* Opportunity Date & time (startDate) */}
                <TableCell sx={{ px: 1 }}>
                  <Typography variant="body2" noWrap>
                    {dayjs(event.startDate).isValid()
                      ? dayjs(event.startDate).format("DD/MM/YYYY hh:mm A")
                      : "-"}
                  </Typography>
                </TableCell>
                {/* Opportunity Fair (price) */}
                <TableCell sx={{ px: 1 }}>
                  <Typography variant="body2">
                    {event.price != null ? `Ã¢â€šÂ¹${event.price}` : "-"}
                  </Typography>
                </TableCell>
                {/* Status */}
                <TableCell sx={{ px: 1 }}>
                  <Chip
                    label={event.status}
                    size="small"
                    sx={{
                      bgcolor: getStatusColor(event.status).bg,
                      color: getStatusColor(event.status).text,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      height: 22,
                    }}
                  />
                </TableCell>
                {/* Actions */}
                <TableCell sx={{ px: 1 }}>
                  <Box display="flex" gap={0.5}>
                    <Tooltip title="View" arrow>
                      <IconButton
                        size="small"
                        sx={{ color: "#1976D2", border: "1px solid #1976D2", borderRadius: 1, p: 0.5 }}
                        onClick={() => handleOpenViewDialog(event)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton
                        size="small"
                        sx={{ color: "#C62828", border: "1px solid #C62828", borderRadius: 1, p: 0.5 }}
                        onClick={() => handleOpenDeleteDialog(event)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Clone" arrow>
                      <IconButton
                        size="small"
                        sx={{ color: "#6D53F4", border: "1px solid #6D53F4", borderRadius: 1, p: 0.5 }}
                        onClick={() => handleOpenDialog("clone", event)}
                      >
                        <CloneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>
        </Box>
      )}

      {/* ================= EDIT/ADD DIALOG ================= */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "#fff",
            p: 3,
            minHeight: "90vh",
          },
        }}
      >
        <DialogTitle sx={{ p: 0, mb: 3, display: "flex", flexDirection: "column" }}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={handleCloseDialog} sx={{ p: 0, mr: 1 }}>
              <BackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight={700} color="#111827">
              {dialogType === "clone" ? "Clone Opportunity" : "Create Opportunity"}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 5, mt: 0.5 }}>
            {dialogType === "clone" ? "Clone from an existing opportunity" : "Create a new opportunity"}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflowX: "hidden" }}>
          

          <Grid container spacing={6} sx={{ ml: 0, width: "100%" }}>
            {/* LEFT COLUMN - FORM FIELDS */}
            <Grid item xs={12} md={5} sx={{ pt: 0 }}>
              <Box display="flex" flexDirection="column" gap={3}>
                <Box>
                  <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                    Opportunity Title <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    inputProps={{ maxLength: 50 }}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" textAlign="right" mt={0.5}>
                    {formData.title.length} / 50
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                    Opportunity Category <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      name="categories"
                      multiple
                      displayEmpty
                      value={formData.categories}
                      onChange={handleCategoryChange}
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return <Typography color="text.secondary">Add Opportunity Category</Typography>;
                        }
                        return selected.join(", ");
                      }}
                      sx={{
                        borderRadius: "12px",
                        bgcolor: "#F3F4F6",
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      }}
                    >
                      <MenuItem disabled value="">
                        <em>Add Opportunity Category</em>
                      </MenuItem>
                      {availableCategories.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                    Details / Description <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    inputProps={{ maxLength: 80 }}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                    }}
                  />
                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Box display="flex" alignItems="center" gap={0.5} color="#6366F1">
                      <InfoIcon sx={{ fontSize: 14 }} />
                      <Typography variant="caption" color="text.secondary">
                        Kindly don't share your phone or email
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {formData.description.length} / 80
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" color="#111827" fontWeight={600}>
                    Opportunity Price Range Min.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    You can select Min, Max, or just Min. or no price as well
                  </Typography>
                  <Box display="flex" gap={2}>
                    <FormControl sx={{ flex: 1 }}>
                      <Select
                        name="priceCurrencyMin"
                        value={formData.priceCurrencyMin}
                        onChange={handleInputChange}
                        displayEmpty
                        sx={{
                          borderRadius: "12px",
                          bgcolor: "#F3F4F6",
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                        }}
                      >
                        <MenuItem value="Currency">Currency</MenuItem>
                        {activeCurrencies.map((c) => (
                          <MenuItem key={c._id} value={c.value}>{c.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      placeholder="Opportunity Price"
                      name="priceRangeMin"
                      value={formData.priceRangeMin}
                      onChange={handleInputChange}
                      sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } },
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                    Opportunity Price Range Max.
                  </Typography>
                  <Box display="flex" gap={2}>
                    <FormControl sx={{ flex: 1 }}>
                      <Select
                        name="priceCurrencyMax"
                        value={formData.priceCurrencyMax}
                        onChange={handleInputChange}
                        displayEmpty
                        sx={{
                          borderRadius: "12px",
                          bgcolor: "#F3F4F6",
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                        }}
                      >
                        <MenuItem value="Currency">Currency</MenuItem>
                        {activeCurrencies.map((c) => (
                          <MenuItem key={c._id} value={c.value}>{c.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      placeholder="Opportunity Price"
                      name="priceRangeMax"
                      value={formData.priceRangeMax}
                      onChange={handleInputChange}
                      error={Boolean(formData.priceRangeMin && formData.priceRangeMax && parseFloat(formData.priceRangeMax) < parseFloat(formData.priceRangeMin))}
                      helperText={formData.priceRangeMin && formData.priceRangeMax && parseFloat(formData.priceRangeMax) < parseFloat(formData.priceRangeMin) ? "Max price < Min price" : ""}
                      sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } },
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                    Discount Range
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter discount in %"
                    name="discountRange"
                    value={formData.discountRange}
                    onChange={handleInputChange}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } },
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                    Final Price
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="e.g. INR 20000"
                    name="finalPrice"
                    value={formData.finalPrice}
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } },
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                    Opportunity Booking Link
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter URL"
                    name="bookingLink"
                    value={formData.bookingLink}
                    onChange={handleInputChange}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } },
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                    Manage Opportunity Type
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      name="manageEventType"
                      value={formData.manageEventType || "REGULAR"}
                      onChange={handleInputChange}
                      sx={{
                        borderRadius: "12px",
                        bgcolor: "#F3F4F6",
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      }}
                    >
                      {activeEventTypes.map((t) => (
                        <MenuItem key={t._id} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                    Opportunity Status
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      name="status"
                      value={formData.status || ""}
                      onChange={handleInputChange}
                      displayEmpty
                      sx={{
                        borderRadius: "12px",
                        bgcolor: "#F3F4F6",
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      }}
                    >
                      <MenuItem disabled value="">
                        <em>Select Opportunity Status</em>
                      </MenuItem>
                      {["UPCOMING", "ONGOING", "COMPLETED", "CANCELLED", "POSTPONED"].map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                    Opportunity Booking Occupancy
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter No. of Occupancy"
                    name="occupancy"
                    value={formData.occupancy}
                    onChange={handleInputChange}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } },
                    }}
                  />
                </Box>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box>
                    <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                      Opportunity Start Date & Time <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <DateTimeField
                      value={formData.startDate ? dayjs(formData.startDate) : null}
                      onChange={(newValue) => {
                        setFormData((prev) => {
                          if (newValue === null) {
                            return { ...prev, startDate: "" };
                          }

                          if (
                            typeof newValue?.isValid !== "function" ||
                            !newValue.isValid()
                          ) {
                            return prev;
                          }

                          return {
                            ...prev,
                            startDate: newValue.toISOString(),
                          };
                        });
                      }}
                      format="DD/MM/YYYY hh:mm A"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {
                            "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } },
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                      Opportunity End Date & Time
                    </Typography>
                    <DateTimeField
                      value={formData.endDate ? dayjs(formData.endDate) : null}
                      onChange={(newValue) => {
                        setFormData((prev) => {
                          if (newValue === null) {
                            return { ...prev, endDate: "" };
                          }

                          if (
                            typeof newValue?.isValid !== "function" ||
                            !newValue.isValid()
                          ) {
                            return prev;
                          }

                          return {
                            ...prev,
                            endDate: newValue.toISOString(),
                          };
                        });
                      }}
                      format="DD/MM/YYYY hh:mm A"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {
                            "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } },
                          },
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              </Box>
            </Grid>

            {/* RIGHT COLUMN - MEDIA & MAP */}
            <Grid item xs={12} md={7} sx={{ pt: 0 }}>
              <Box display="flex" flexDirection="column" gap={4}>

                {/* Image Upload & Preview Section */}
                <Box display="flex" gap={4} width="100%" alignItems="flex-start">

                  {/* Upload Box */}
                  <Box flex={1}>
                    <Typography variant="body2" color="#111827" fontWeight={600} mb={1} textAlign="left">
                      Upload Cover Poster <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Box
                      component="label"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        height: "280px",
                        border: "2px dashed #E5E7EB",
                        borderRadius: "16px",
                        cursor: "pointer",
                        bgcolor: "#fff",
                        transition: "all 0.2s",
                        "&:hover": { borderColor: "#A855F7", bgcolor: "#F9FAFB" },
                      }}
                    >
                      <input hidden type="file" multiple accept="image/*,video/mp4,video/quicktime,video/webm,video/mpeg" onChange={handleImageUpload} />
                      {uploadingImage ? (
                        <CircularProgress />
                      ) : (
                        <>
                          <AddIcon sx={{ fontSize: 40, color: "#9CA3AF", mb: 2 }} />
                          <Typography variant="body2" color="text.secondary" textAlign="center">
                            Drag and Drop here or<br />Click to upload
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem", display: "block", mt: 1, textAlign: "left" }}>
                      PNG, JPG, GIF, MP4, MOV, WebM. Max. 50MB
                    </Typography>
                  </Box>

                  {/* Preview Box */}
                  <Box flex={1}>
                    <Typography variant="body2" color="#111827" fontWeight={600} mb={1} textAlign="left">
                      Preview <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        height: "280px",
                        borderRadius: "16px",
                        overflow: "hidden",
                        border: "1px solid #E5E7EB",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      }}
                    >
                      {formData.images.length > 0 ? (
                        (() => {
                          const primary = formData.images.find(img => img.isPrimary) || formData.images[0];
                          return primary.type === 'video' ? (
                            <video
                              src={primary.url}
                              controls
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <img
                              src={primary.url}
                              alt="Preview"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          );
                        })()
                      ) : (
                        <Box display="flex" alignItems="center" justifyContent="center" height="100%" bgcolor="#F9FAFB">
                          <Typography color="text.disabled">No Image</Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem", display: "block", mt: 1, textAlign: "left" }}>
                      This How the Main Poster looks like on<br />the Thumbnail of the Opportunity post
                    </Typography>
                  </Box>
                </Box>

                {/* Thumbnails Gallery */}
                {formData.images.length > 0 && (
                  <Box width="100%">
                    <Box display="flex" gap={2} flexWrap="wrap" justifyContent="flex-start">
                      {formData.images.map((img, i) => (
                        <Box key={i} display="flex" flexDirection="column" alignItems="center">
                          <Box
                            sx={{
                              width: "100px",
                              height: "140px",
                              borderRadius: "12px",
                              overflow: "hidden",
                              border: "1px solid #E5E7EB",
                              position: "relative",
                              cursor: "grab"
                            }}
                            onClick={() => handleSetPrimaryImage(i)}
                          >
                            {img.type === 'video' ? (
                              <video src={img.url} muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                              <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            )}
                            <Box
                              onClick={(e) => { e.stopPropagation(); handleRemoveImage(i); }}
                              sx={{
                                position: "absolute",
                                top: 6,
                                right: 6,
                                width: 24,
                                height: 24,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "#6366F1",
                                color: "white",
                                borderRadius: "50%",
                                cursor: "pointer",
                                zIndex: 10
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </Box>
                          </Box>
                          {i === 0 && (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.6rem", textAlign: "center", mt: 1, width: "100px", lineHeight: 1.2 }}>
                              Drag and swap for the main poster thumbnail
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Map Details */}
                <Box width="100%" mt={2}>
                  <Box mb={3}>
                    <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                      Map location search <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Autocomplete
                      freeSolo
                      options={locationSuggestions}
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.label || ""
                      }
                      loading={locationSearchLoading}
                      filterOptions={(x) => x}
                      inputValue={formData.mapLocationSearch}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "input") {
                          setFormData((prev) => ({
                            ...prev,
                            mapLocationSearch: newInputValue,
                          }));
                          handleLocationSearch(newInputValue);
                        }
                      }}
                      onChange={(event, newValue) => {
                        if (newValue && typeof newValue !== "string") {
                          setFormData((prev) => ({
                            ...prev,
                            mapLocationSearch: newValue.label,
                            mapSearchTrigger: newValue.label,
                            location: {
                              city:
                                newValue.address?.address?.city ||
                                newValue.address?.address?.town ||
                                newValue.address?.address?.county ||
                                "",
                              country: newValue.address?.address?.country || "",
                              fullAddress: newValue.label,
                              lat: newValue.lat,
                              lng: newValue.lng,
                            },
                          }));
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="Search location..."
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: "#9CA3AF" }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              bgcolor: "#F3F4F6",
                              "& fieldset": { border: "none" },
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.label + option.lat}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LocationIcon sx={{ color: "#6366F1", fontSize: 20 }} />
                            <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                              {option.label}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                  </Box>

                  <Box mb={4}>
                    <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>
                      Map URL <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter URL"
                      name="mapUrl"
                      value={formData.mapUrl}
                      onChange={handleInputChange}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } },
                      }}
                    />
                  </Box>

                  {/* Map Viewer */}
                  <Box
                    sx={{
                      width: "100%",
                      height: "300px",
                      borderRadius: "16px",
                      overflow: "hidden",
                      border: "2px solid #8B5CF6",
                      boxShadow: "0 4px 12px rgba(139, 92, 246, 0.2)",
                    }}
                  >
                    <LocationPicker
                      onLocationSelect={(payload) => {
                        setFormData((prev) => ({
                          ...prev,
                          location: {
                            city: payload.address?.address?.city || payload.address?.address?.county,
                            country: payload.address?.address?.country,
                            fullAddress: payload.address?.display_name,
                            lat: payload.lat,
                            lng: payload.lng,
                          },
                        }));
                      }}
                      locationValue={formData.location.lat && formData.location.lng ? `${formData.location.lat},${formData.location.lng}` : ""}
                      searchQuery={formData.mapSearchTrigger}
                    />
                  </Box>
                </Box>

              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 0, mt: 5 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            sx={{
              bgcolor: "#6366F1",
              "&:hover": { bgcolor: "#4F46E5" },
              borderRadius: "12px",
              py: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1.1rem",
            }}
          >
            Publish Opportunity
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= VIEW DIALOG ================= */}
      <Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "#fff",
            p: 3,
            minHeight: "90vh",
          },
        }}
      >
        {selectedEvent && (
          <>
            {/* Header */}
            <DialogTitle sx={{ p: 0, mb: 2, display: "flex", flexDirection: "column" }}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton onClick={handleCloseViewDialog} sx={{ p: 0, mr: 1 }}>
                    <BackIcon />
                  </IconButton>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="#111827">
                      View Opportunity
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      You can view or edit the upcoming opportunities
                    </Typography>
                  </Box>
                </Box>
                {isEditingView ? (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setIsEditingView(false);
                      setViewFormData(buildViewFormData(selectedEvent));
                    }}
                    sx={{
                      borderRadius: "20px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 4,
                      py: 1,
                      color: "#6B7280",
                      borderColor: "#D1D5DB",
                      "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB" },
                    }}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => setIsEditingView(true)}
                    sx={{
                      borderRadius: "20px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 5,
                      py: 1.2,
                      background: "linear-gradient(90deg, #9B59F0 0%, #E07BAA 50%, #F0A070 100%)",
                      color: "#fff",
                      boxShadow: "none",
                      "&:hover": {
                        background: "linear-gradient(90deg, #8B45E0 0%, #D06B9A 50%, #E09060 100%)",
                        boxShadow: "0 4px 14px rgba(155, 89, 240, 0.3)",
                      },
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, overflowX: "hidden" }}>
              {/* Mode Chip */}
              <Box sx={{ mb: 3, ml: 5 }}>
                <Chip
                  label={viewFormData.mode || selectedEvent.mode}
                  sx={{
                    bgcolor: "#6366F1",
                    color: "#fff",
                    fontWeight: 600,
                    borderRadius: "20px",
                    px: 2,
                    fontSize: "0.85rem",
                  }}
                />
              </Box>

              <Grid container spacing={6} sx={{ ml: 0, width: "100%" }}>
                {/* LEFT COLUMN - EVENT DETAILS */}
                <Grid item xs={12} md={5} sx={{ pt: 0 }}>
                  <Box display="flex" flexDirection="column" gap={2.5}>

                    {/* Brand Logo */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Brand Logo</Typography>
                      {selectedEvent.images?.[0] ? (
                        <Box component="img" src={selectedEvent.images[0].url} alt="Logo" sx={{ width: 60, height: 60, borderRadius: "12px", objectFit: "cover", border: "2px solid #E5E7EB" }} />
                      ) : (
                        <Box sx={{ width: 60, height: 60, borderRadius: "12px", bgcolor: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography color="text.disabled" variant="caption">N/A</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Brand Name */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Brand Name</Typography>
                      {isEditingView ? (
                        <TextField fullWidth name="title" value={viewFormData.title} onChange={handleViewInputChange} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.title}</Typography>
                      )}
                    </Box>

                    {/* Opportunity Title */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity Title</Typography>
                      {isEditingView ? (
                        <TextField fullWidth name="title" value={viewFormData.title} onChange={handleViewInputChange} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.title}</Typography>
                      )}
                    </Box>

                    {/* Opportunity Category */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity Category</Typography>
                      {isEditingView ? (
                        <FormControl fullWidth size="small">
                          <Select name="categories" multiple value={viewFormData.categories} onChange={handleViewCategoryChange}
                            renderValue={(sel) => sel.length === 0 ? <Typography color="text.secondary">Select</Typography> : sel.join(", ")}
                            sx={{ borderRadius: "12px" }}>
                            {availableCategories.map((cat) => (<MenuItem key={cat} value={cat}>{cat}</MenuItem>))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.categories?.join(", ") || "-"}</Typography>
                      )}
                    </Box>

                    {/* Details / Description */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Details / Description</Typography>
                      {isEditingView ? (
                        <TextField fullWidth multiline rows={3} name="description" value={viewFormData.description} onChange={handleViewInputChange} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600} sx={{ whiteSpace: "pre-wrap" }}>{selectedEvent.description}</Typography>
                      )}
                    </Box>

                    {/* Opportunity Price Range Min. */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity Price Range Min.</Typography>
                      {isEditingView ? (
                        <Box display="flex" gap={1}>
                          <FormControl sx={{ flex: 1 }} size="small">
                            <Select name="priceCurrencyMin" value={viewFormData.priceCurrencyMin} onChange={handleViewInputChange} sx={{ borderRadius: "12px" }}>
                              <MenuItem value="Currency">Currency</MenuItem>
                              {activeCurrencies.map((c) => (
                                <MenuItem key={c._id} value={c.value}>{c.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField placeholder="Min Price" name="priceRangeMin" value={viewFormData.priceRangeMin} onChange={handleViewInputChange} size="small" sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                        </Box>
                      ) : (
                        <Typography variant="body1" fontWeight={700}>
                          {selectedEvent.priceRangeMin != null && selectedEvent.priceRangeMin !== ""
                            ? `Min. ${selectedEvent.priceCurrencyMin && selectedEvent.priceCurrencyMin !== "Currency" ? selectedEvent.priceCurrencyMin + " " : ""}${selectedEvent.priceRangeMin}${selectedEvent.priceRangeMax != null && selectedEvent.priceRangeMax !== "" ? ` Ã¢â‚¬â€œ Max. ${selectedEvent.priceCurrencyMax && selectedEvent.priceCurrencyMax !== "Currency" ? selectedEvent.priceCurrencyMax + " " : ""}${selectedEvent.priceRangeMax}` : ""}`
                            : `Min. ${selectedEvent.price || "-"}`}
                        </Typography>
                      )}
                    </Box>

                    {/* Discount Range */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Discount Range</Typography>
                      {isEditingView ? (
                        <TextField fullWidth placeholder="e.g. 20%" name="discountRange" value={viewFormData.discountRange} onChange={handleViewInputChange} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.discountRange || "-"}</Typography>
                      )}
                    </Box>

                    {/* Final Price */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Final Price</Typography>
                      {isEditingView ? (
                        <TextField fullWidth placeholder="e.g. INR 20000" name="finalPrice" value={viewFormData.finalPrice} InputProps={{ readOnly: true }} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.finalPrice || selectedEvent.price || "-"}</Typography>
                      )}
                    </Box>

                    {/* Opportunity Booking Link */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity Booking Link</Typography>
                      {isEditingView ? (
                        <TextField fullWidth placeholder="Enter URL" name="bookingLink" value={viewFormData.bookingLink} onChange={handleViewInputChange} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.bookingLink ? `URI: ${selectedEvent.bookingLink}` : "-"}</Typography>
                      )}
                    </Box>

                    {/* Manage Offer Type */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Manage Offer Type</Typography>
                      {isEditingView ? (
                        <FormControl fullWidth size="small">
                          <Select name="manageEventType" value={viewFormData.manageEventType} onChange={handleViewInputChange} sx={{ borderRadius: "12px" }}>
                            {activeEventTypes.map((t) => (
                              <MenuItem key={t._id} value={t.value}>{t.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.manageEventType || selectedEvent.eventType || "-"}</Typography>
                      )}
                    </Box>

                    {/* Opportunity Booking Occupancy Limits */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity Booking Occupancy Limits</Typography>
                      {isEditingView ? (
                        <TextField fullWidth name="occupancy" value={viewFormData.occupancy} onChange={handleViewInputChange} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.occupancy || "-"}</Typography>
                      )}
                    </Box>

                    {/* Opportunity Start Date */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity Start Date</Typography>
                      {isEditingView ? (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimeField
                            value={viewFormData.startDate ? dayjs(viewFormData.startDate) : null}
                            onChange={(newValue) => {
                              if (newValue && typeof newValue?.isValid === "function" && newValue.isValid()) {
                                setViewFormData((prev) => ({ ...prev, startDate: newValue.toISOString() }));
                              }
                            }}
                            format="DD-MM-YYYY hh:mm A"
                            slotProps={{ textField: { fullWidth: true, size: "small", sx: { "& .MuiOutlinedInput-root": { borderRadius: "12px" } } } }}
                          />
                        </LocalizationProvider>
                      ) : (
                        <Typography variant="body1" fontWeight={600}>
                          {dayjs(selectedEvent.startDate).isValid() ? dayjs(selectedEvent.startDate).format("DD-MM-YYYY") : "-"}
                        </Typography>
                      )}
                    </Box>

                    {/* Opportunity End Date */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity End Date</Typography>
                      {isEditingView ? (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimeField
                            value={viewFormData.endDate ? dayjs(viewFormData.endDate) : null}
                            onChange={(newValue) => {
                              if (newValue && typeof newValue?.isValid === "function" && newValue.isValid()) {
                                setViewFormData((prev) => ({ ...prev, endDate: newValue.toISOString() }));
                              }
                            }}
                            format="DD-MM-YYYY hh:mm A"
                            slotProps={{ textField: { fullWidth: true, size: "small", sx: { "& .MuiOutlinedInput-root": { borderRadius: "12px" } } } }}
                          />
                        </LocalizationProvider>
                      ) : (
                        <Typography variant="body1" fontWeight={600}>
                          {dayjs(selectedEvent.endDate).isValid() ? dayjs(selectedEvent.endDate).format("DD-MM-YYYY") : "-"}
                        </Typography>
                      )}
                    </Box>

                  </Box>
                </Grid>

                {/* RIGHT COLUMN - MEDIA & MAP */}
                <Grid item xs={12} md={7} sx={{ pt: 0 }}>
                  <Box display="flex" flexDirection="column" gap={3}>

                    {/* Preview */}
                    <Box>
                      <Typography variant="body2" color="#111827" fontWeight={600} mb={1} textAlign="center">Preview</Typography>
                      <Box sx={{ width: "100%", height: "280px", borderRadius: "16px", overflow: "hidden", border: "1px solid #E5E7EB", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
                        {(() => {
                          const primary = selectedEvent.images?.find(img => img.isPrimary) || selectedEvent.images?.[0];
                          if (!primary) return (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%" bgcolor="#F9FAFB">
                              <Typography color="text.disabled">No Image</Typography>
                            </Box>
                          );
                          return primary.type === "video" || getMediaType(primary.url) === "video" ? (
                            <video src={primary.url} controls style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <img src={primary.url} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          );
                        })()}
                      </Box>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem", display: "block", mt: 1, textAlign: "center" }}>
                        This How the Main Poster looks like on<br />the Thumbnail of the Opportunity post
                      </Typography>
                    </Box>

                    {/* Gallery Thumbnails */}
                    {selectedEvent.images && selectedEvent.images.length > 0 && (
                      <Box>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {selectedEvent.images.map((img, i) => (
                            <Box key={i} sx={{ width: "100px", height: "140px", borderRadius: "12px", overflow: "hidden", border: img.isPrimary ? "3px solid #6366F1" : "1px solid #E5E7EB" }}>
                              {img.type === "video" || getMediaType(img.url) === "video" ? (
                                <video src={img.url} muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              )}
                            </Box>
                          ))}
                        </Box>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.6rem", display: "block", mt: 1 }}>
                          Drag and swap for the main poster thumbnail
                        </Typography>
                      </Box>
                    )}

                    {/* Map Location Search */}
                    <Box>
                      <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>Map location search</Typography>
                      {isEditingView ? (
                        <Autocomplete
                          freeSolo
                          options={locationSuggestions}
                          getOptionLabel={(option) => typeof option === "string" ? option : option.label || ""}
                          loading={locationSearchLoading}
                          filterOptions={(x) => x}
                          inputValue={viewFormData.mapLocationSearch}
                          onInputChange={(event, newInputValue, reason) => {
                            if (reason === "input") {
                              setViewFormData((prev) => ({ ...prev, mapLocationSearch: newInputValue }));
                              handleLocationSearch(newInputValue);
                            }
                          }}
                          onChange={(event, newValue) => {
                            if (newValue && typeof newValue !== "string") {
                              setViewFormData((prev) => ({
                                ...prev,
                                mapLocationSearch: newValue.label,
                                mapSearchTrigger: newValue.label,
                                location: {
                                  city: newValue.address?.address?.city || newValue.address?.address?.town || newValue.address?.address?.county || "",
                                  country: newValue.address?.address?.country || "",
                                  fullAddress: newValue.label,
                                  lat: newValue.lat,
                                  lng: newValue.lng,
                                },
                              }));
                            }
                          }}
                          renderInput={(params) => (
                            <TextField {...params} fullWidth placeholder="Search location..." size="small"
                              InputProps={{ ...params.InputProps, startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: "#9CA3AF" }} /></InputAdornment>) }}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } } }}
                            />
                          )}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>
                          {selectedEvent.location?.fullAddress || `${selectedEvent.location?.city || ""}, ${selectedEvent.location?.country || ""}`}
                        </Typography>
                      )}
                    </Box>

                    {/* Map URL */}
                    <Box>
                      <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>Map URL</Typography>
                      {isEditingView ? (
                        <TextField fullWidth placeholder="Enter URL" name="mapUrl" value={viewFormData.mapUrl} onChange={handleViewInputChange} size="small"
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } } }}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.mapUrl ? `URI: ${selectedEvent.mapUrl}` : "-"}</Typography>
                      )}
                    </Box>

                    {/* Map Embed */}
                    <Box sx={{ width: "100%", height: "300px", borderRadius: "16px", overflow: "hidden", border: "2px solid #8B5CF6", boxShadow: "0 4px 12px rgba(139, 92, 246, 0.2)" }}>
                      {isEditingView ? (
                        <LocationPicker
                          onLocationSelect={(payload) => {
                            setViewFormData((prev) => ({
                              ...prev,
                              location: {
                                city: payload.address?.address?.city || payload.address?.address?.county,
                                country: payload.address?.address?.country,
                                fullAddress: payload.address?.display_name,
                                lat: payload.lat,
                                lng: payload.lng,
                              },
                            }));
                          }}
                          locationValue={viewFormData.location?.lat && viewFormData.location?.lng ? `${viewFormData.location.lat},${viewFormData.location.lng}` : ""}
                          searchQuery={viewFormData.mapSearchTrigger}
                        />
                      ) : (
                        (() => {
                          const lat = selectedEvent.location?.geo?.coordinates?.[1];
                          const lng = selectedEvent.location?.geo?.coordinates?.[0];
                          if (lat && lng) {
                            return (
                              <iframe
                                title="Opportunity Location"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01},${lat - 0.01},${lng + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat},${lng}`}
                              />
                            );
                          }
                          return (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%" bgcolor="#F9FAFB">
                              <Typography color="text.disabled">No location data</Typography>
                            </Box>
                          );
                        })()
                      )}
                    </Box>

                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            {/* Bottom Action Button */}
            <DialogActions sx={{ p: 0, mt: 4 }}>
              <Button
                variant="contained"
                onClick={isEditingView ? handleSaveEditedEvent : handleCloseViewDialog}
                fullWidth
                disabled={savingEvent}
                sx={{
                  bgcolor: "#6366F1",
                  "&:hover": { bgcolor: "#4F46E5" },
                  borderRadius: "12px",
                  py: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1.1rem",
                }}
              >
                {savingEvent ? <CircularProgress size={24} color="inherit" /> : isEditingView ? "Save Opportunity" : "Close"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ================= DELETE CONFIRMATION DIALOG ================= */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 4,
            textAlign: "center",
          },
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827" gutterBottom>
          Please double-check before deleting this opportunity
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          This action can be undone, but all opportunity-related data will be removed
          from the mobile application.
        </Typography>
        <Box display="flex" justifyContent="center" gap={2}>
          <Button
            variant="contained"
            onClick={handleDeleteOpportunity}
            sx={{
              px: 4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#C62828",
              "&:hover": { bgcolor: "#B71C1C" },
            }}
          >
            Delete
          </Button>
          <Button
            variant="outlined"
            onClick={handleCloseDeleteDialog}
            sx={{
              px: 4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default OpportunityManagement;


